import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, Heart, Shield, ExternalLink, Loader2 } from "lucide-react";
import { useConfig } from "@/hooks/useConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLE = {
  baixo: { color: "#10B981", label: "Poucos sinais de atenção", icon: CheckCircle2 },
  moderado: { color: "#F59E0B", label: "Alguns sinais de atenção", icon: AlertCircle },
  elevado: { color: "#EF4444", label: "Vários sinais de atenção", icon: AlertCircle },
};

export default function Triagem() {
  const config = useConfig();
  const WHATSAPP_URL = config?.whatsapp_url || "";
  const [step, setStep] = useState("intro"); // intro | dados | perguntas | resultado
  const [questions, setQuestions] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [options, setOptions] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [form, setForm] = useState({
    nome_responsavel: "",
    email: "",
    nome_crianca: "",
    idade_anos: "",
    idade_meses: "",
    consent: false,
  });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/triagem/questions`).then((r) => {
      setQuestions(r.data.questions);
      setBlocks(r.data.blocks || []);
      setOptions(r.data.options);
      setDisclaimer(r.data.disclaimer);
    }).catch(() => setError("Não foi possível carregar o questionário."));
  }, []);

  const totalIdadeMeses = () => {
    const anos = parseInt(form.idade_anos || "0", 10) || 0;
    const meses = parseInt(form.idade_meses || "0", 10) || 0;
    return anos * 12 + meses;
  };

  const canContinueDados = () =>
    form.nome_responsavel.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    totalIdadeMeses() >= 6 &&
    totalIdadeMeses() <= 180 &&
    form.consent;

  const progress = () =>
    questions.length > 0 ? Math.round((currentQ / questions.length) * 100) : 0;

  const selectAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    // auto-advance com pequena pausa
    setTimeout(() => {
      if (currentQ < questions.length - 1) {
        setCurrentQ(currentQ + 1);
      }
    }, 250);
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        nome_responsavel: form.nome_responsavel.trim(),
        email: form.email.trim().toLowerCase(),
        nome_crianca: form.nome_crianca.trim() || null,
        idade_meses: totalIdadeMeses(),
        consent: form.consent,
        answers: questions.map((q) => ({
          question_id: q.id,
          value: answers[q.id] ?? 1,
        })),
      };
      const { data } = await axios.post(`${API}/triagem/submit`, payload);
      setResult(data);
      setStep("resultado");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao enviar triagem. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------- RENDER ----------------------
  return (
    <div className="min-h-screen bg-background" data-testid="triagem-page">
      {/* Top bar */}
      <header className="border-b border-border bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="container-ga py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-ga-ink/70 hover:text-ga-ink transition-colors"
            data-testid="triagem-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao site
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-ga-emerald flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <div className="font-display font-black text-ga-ink">GA<span className="text-ga-emerald">+</span></div>
            <div className="text-xs text-ga-ink/60 hidden sm:block">· ENAGA</div>
          </div>
        </div>
      </header>

      <main className="container-ga py-10 sm:py-14 max-w-3xl">
        {error && (
          <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="triagem-error">
            {error}
          </div>
        )}

        {/* ---------------- INTRO ---------------- */}
        {step === "intro" && (
          <section data-testid="triagem-intro">
            <div className="pill border-ga-amber/40 text-ga-amber_deep bg-ga-amber/10 mb-6">
              <Shield className="w-3.5 h-3.5" />
              Triagem orientativa
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ga-ink leading-[1.05]">
              Um olhar atento para o <span className="text-ga-emerald">desenvolvimento</span> da sua criança.
            </h1>
            <p className="mt-6 text-lg text-ga-ink/70 leading-relaxed">
              Responda a 15 perguntas rápidas sobre <strong>comunicação, interação social, comportamento, atenção</strong> e <strong>emoções</strong>. Depois, receba no seu e-mail uma orientação sobre possíveis sinais de atenção no desenvolvimento.
            </p>

            <div className="mt-8 p-6 rounded-3xl bg-ga-amber/5 border-2 border-ga-amber/30">
              <div className="font-display font-bold text-ga-ink mb-3">Antes de começar, é importante entender:</div>
              <ul className="space-y-2.5 text-[15px] text-ga-ink/80 leading-relaxed">
                <li>• Esta é uma ferramenta de <strong>triagem orientativa</strong>.</li>
                <li>• Ela <strong>não confirma</strong> e <strong>não exclui</strong> diagnósticos.</li>
                <li>• O resultado <strong>não substitui avaliação profissional</strong>.</li>
                <li>• Se houver sinais de atenção, recomendamos procurar um profissional qualificado.</li>
                <li>• O ENAGA pode te orientar na busca pelo acompanhamento adequado.</li>
              </ul>
            </div>

            <button
              type="button"
              onClick={() => setStep("dados")}
              className="btn-primary mt-8"
              data-testid="triagem-btn-start"
            >
              Começar triagem
              <ArrowRight className="w-4 h-4" />
            </button>
          </section>
        )}

        {/* ---------------- DADOS ---------------- */}
        {step === "dados" && (
          <section data-testid="triagem-dados">
            <div className="pill border-ga-emerald/40 text-ga-emerald bg-ga-emerald/10 mb-5">
              Passo 1 de 2 · Seus dados
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ga-ink">
              Quem é você e a criança?
            </h2>
            <p className="mt-3 text-ga-ink/70">
              Essas informações nos ajudam a enviar o resultado por e-mail. Não compartilhamos seus dados.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-ga-ink mb-1.5">Seu nome *</label>
                <input
                  type="text"
                  value={form.nome_responsavel}
                  onChange={(e) => setForm({ ...form, nome_responsavel: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:border-ga-emerald focus:ring-2 focus:ring-ga-emerald/20 outline-none transition"
                  placeholder="Nome completo do responsável"
                  data-testid="triagem-input-nome"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-ink mb-1.5">Seu e-mail *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:border-ga-emerald focus:ring-2 focus:ring-ga-emerald/20 outline-none transition"
                  placeholder="seu@email.com"
                  data-testid="triagem-input-email"
                />
                <p className="text-xs text-ga-ink/50 mt-1.5">Enviaremos o resultado orientativo para este e-mail.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-ink mb-1.5">Nome da criança (opcional)</label>
                <input
                  type="text"
                  value={form.nome_crianca}
                  onChange={(e) => setForm({ ...form, nome_crianca: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:border-ga-emerald focus:ring-2 focus:ring-ga-emerald/20 outline-none transition"
                  placeholder="Ex: Maria"
                  data-testid="triagem-input-nome-crianca"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-ink mb-1.5">Idade da criança *</label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="number"
                      min="0" max="15"
                      value={form.idade_anos}
                      onChange={(e) => setForm({ ...form, idade_anos: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:border-ga-emerald focus:ring-2 focus:ring-ga-emerald/20 outline-none transition"
                      placeholder="0"
                      data-testid="triagem-input-idade-anos"
                    />
                    <div className="text-xs text-ga-ink/60 mt-1 text-center">anos</div>
                  </div>
                  <div className="flex-1">
                    <input
                      type="number"
                      min="0" max="11"
                      value={form.idade_meses}
                      onChange={(e) => setForm({ ...form, idade_meses: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:border-ga-emerald focus:ring-2 focus:ring-ga-emerald/20 outline-none transition"
                      placeholder="0"
                      data-testid="triagem-input-idade-meses"
                    />
                    <div className="text-xs text-ga-ink/60 mt-1 text-center">meses</div>
                  </div>
                </div>
                <p className="text-xs text-ga-ink/50 mt-1.5">Entre 6 meses e 15 anos.</p>
              </div>

              <label className="flex items-start gap-3 p-4 rounded-xl bg-ga-amber/5 border border-ga-amber/30 cursor-pointer" data-testid="triagem-consent-label">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-ga-emerald"
                  data-testid="triagem-consent-checkbox"
                />
                <span className="text-sm text-ga-ink/80 leading-relaxed">
                  Entendo que este é um instrumento <strong>orientativo</strong>, não confirma nem exclui diagnóstico, e <strong>não substitui avaliação profissional</strong>.
                </span>
              </label>
            </div>

            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setStep("intro")}
                className="btn-outline"
                data-testid="triagem-btn-back-intro"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>
              <button
                type="button"
                disabled={!canContinueDados()}
                onClick={() => setStep("perguntas")}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="triagem-btn-continue-perguntas"
              >
                Continuar
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </section>
        )}

        {/* ---------------- PERGUNTAS ---------------- */}
        {step === "perguntas" && questions.length > 0 && (
          <section data-testid="triagem-perguntas">
            <div className="flex items-center justify-between mb-2">
              <div className="pill border-ga-emerald/40 text-ga-emerald bg-ga-emerald/10">
                Passo 2 de 2 · Questionário
              </div>
              <div className="text-sm text-ga-ink/60">
                {currentQ + 1} de {questions.length}
              </div>
            </div>
            <div className="w-full h-1.5 bg-ga-ink/10 rounded-full overflow-hidden mb-6">
              <div
                className="h-full bg-ga-emerald transition-all duration-300"
                style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }}
              />
            </div>

            {(() => {
              const curr = questions[currentQ];
              const block = blocks.find((b) => b.id === curr.block);
              return (
                <>
                  {block && (
                    <div className="mb-4" data-testid={`triagem-block-${block.id}`}>
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-ga-amber_deep">
                        Bloco · {block.title}
                      </div>
                      <div className="text-sm text-ga-ink/55 mt-0.5">{block.subtitle}</div>
                    </div>
                  )}
                  <h2 className="font-display font-bold text-2xl sm:text-3xl text-ga-ink leading-snug" data-testid={`triagem-question-${curr.id}`}>
                    {curr.text}
                  </h2>
                </>
              );
            })()}

            <div className="mt-8 space-y-3">
              {options.map((o) => {
                const selected = answers[questions[currentQ].id] === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => selectAnswer(questions[currentQ].id, o.value)}
                    className={`w-full text-left px-6 py-4 rounded-2xl border-2 transition-all ${
                      selected
                        ? "border-ga-emerald bg-ga-emerald/10 text-ga-ink"
                        : "border-border bg-white text-ga-ink/80 hover:border-ga-emerald/50"
                    }`}
                    data-testid={`triagem-answer-${questions[currentQ].id}-${o.value}`}
                  >
                    <span className="font-medium">{o.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => {
                  if (currentQ > 0) setCurrentQ(currentQ - 1);
                  else setStep("dados");
                }}
                className="btn-outline"
                data-testid="triagem-btn-back-question"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>
              {currentQ < questions.length - 1 ? (
                <button
                  type="button"
                  disabled={answers[questions[currentQ].id] === undefined}
                  onClick={() => setCurrentQ(currentQ + 1)}
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="triagem-btn-next-question"
                >
                  Próxima
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  disabled={answers[questions[currentQ].id] === undefined || submitting}
                  onClick={submit}
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="triagem-btn-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Ver meu resultado
                </button>
              )}
            </div>
          </section>
        )}

        {/* ---------------- RESULTADO ---------------- */}
        {step === "resultado" && result && (
          <section data-testid="triagem-resultado">
            {(() => {
              const LV = LEVEL_STYLE[result.result.level];
              const Icon = LV.icon;
              return (
                <>
                  <div
                    className="p-6 sm:p-8 rounded-3xl border-2"
                    style={{ borderColor: LV.color, background: `${LV.color}0D` }}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-6 h-6" style={{ color: LV.color }} />
                      <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: LV.color }}>
                        Nível orientativo
                      </div>
                    </div>
                    <div className="mt-3 font-display font-black text-3xl sm:text-4xl text-ga-ink tracking-tight">
                      {LV.label}
                    </div>
                    <div className="mt-2 text-sm text-ga-ink/60">
                      Pontuação: {result.result.score} de {result.result.max_score}
                    </div>
                  </div>

                  <div className="mt-6 space-y-3 text-[17px] text-ga-ink/85 leading-relaxed" data-testid="triagem-result-message">
                    {result.result.message.split("\n\n").map((p, i) => (
                      <p key={i}>{p.replace(/\*\*/g, "")}</p>
                    ))}
                  </div>

                  <div className="mt-8 p-5 rounded-2xl bg-ga-amber/10 border-l-4 border-ga-amber text-sm text-ga-ink/80 leading-relaxed">
                    <strong>Importante:</strong> esta é uma ferramenta de triagem orientativa. Ela <strong>não confirma</strong> e <strong>não exclui</strong> qualquer diagnóstico. Este resultado <strong>não substitui avaliação profissional</strong>.
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary"
                      data-testid="triagem-cta-whatsapp"
                    >
                      {result.result.cta_label}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <Link to="/" className="btn-outline" data-testid="triagem-btn-home">
                      Voltar ao site
                    </Link>
                  </div>

                  <p className="mt-6 text-xs text-ga-ink/50" data-testid="triagem-email-status">
                    {result.email_sent
                      ? "✓ Enviamos o resultado também para o seu e-mail."
                      : "⚠ Não conseguimos enviar o e-mail agora, mas o resultado está aqui acima."}
                  </p>
                </>
              );
            })()}
          </section>
        )}

        <p className="mt-16 text-xs text-ga-ink/40 leading-relaxed max-w-2xl">
          {disclaimer}
        </p>
      </main>
    </div>
  );
}
